// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Tithe — bootstrap and the screen machine. Owns which screen is up, the room's Net (created ONCE
// per session and never left for a rematch), the tap machine that turns two taps into one packed
// ply, and the setInterval that drives every clock. Rules live in rules.ts, the search in bot.ts,
// the round in duel.ts, the board in render.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type RoundPlayer, type Rounds } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';
import { newSeed } from '@ben-gy/game-engine/rng';

import { escapeHtml } from './dom';
import { MODE_IDS, modeOf } from './modes';
import {
  COFFER,
  PH_CHOOSE,
  PH_PLAY,
  PH_SPLIT,
  PPS,
  cellName,
  isKeeper,
  isOver,
  packChoose,
  packSplit,
  partitions,
  plyFrom,
  plyHand,
  plyKind,
  plyTake,
  plyTo,
  seatOfPiece,
  K_MOVE,
  K_SLACK,
  type PlyEvent,
  type State,
} from './rules';
import { tollOf } from './tolls';
import { DEFAULT_TIER, chooseAny, optsForTier, tierOf } from './bot';
import { Duel, seatForRound, type WireMsg } from './duel';
import { BoardView, emptyHints, tollHtml, type LastMove, type ViewHints } from './render';
import { runCountdown } from './countdown';
import { analyse, missedNote } from './analysis';
import { ATTRIB, aboutHtml, helpHtml, menuHtml, resultsHtml } from './ui';
import { PALETTE, SEAT_COLOUR, SEAT_NAME } from './palette';

const SLUG = 'tithe';

const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

const playerName = resolveName(store, () => `Player ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;
let myTier = tierOf(store.get<string>('tier', DEFAULT_TIER)).id;

const recordKey = (id: string): string => `rec_${id}`;
function recordFor(id: string): [number, number] {
  const v = store.get<number[]>(recordKey(id), [0, 0]) ?? [0, 0];
  return [v[0] | 0, v[1] | 0];
}
function noteResult(id: string, won: boolean): void {
  const r = recordFor(id);
  store.set(recordKey(id), won ? [r[0] + 1, r[1]] : [r[0], r[1] + 1]);
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let duel: Duel | null = null;
let roster: RoundPlayer[] = [];
let tally: [number, number] = [0, 0];
let roomCode = '';
let roundNo = 0;
let soloRound = 0;

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet">${html}</div>`;
  document.body.appendChild(wrap);
  const close = (): void => wrap.remove();
  const armedAt = Date.now();
  wrap.addEventListener('pointerdown', (e) => {
    // A sheet opened from a pointerup would otherwise be closed by that gesture's trailing click.
    if (e.target === wrap && Date.now() - armedAt > 350) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

function ping(cue: string, pitch?: number): void {
  try {
    sfx.play(cue, pitch === undefined ? undefined : { pitch });
  } catch {
    /* audio is best-effort and must never break a turn */
  }
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, tierId: myTier, record: recordFor(myModeChoice), muted: sfx.muted() });

  content.querySelectorAll<HTMLElement>('.chip[data-mode]').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      ping('select');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLElement>('.chip[data-tier]').forEach((c) =>
    c.addEventListener('click', () => {
      myTier = tierOf(c.dataset.tier).id;
      store.set('tier', myTier);
      ping('select');
      showMenu();
    }),
  );
  content.querySelector('#playSolo')!.addEventListener('click', () => startSolo());
  content.querySelector('#playFriends')!.addEventListener('click', () => showRoomEntry());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml()));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml());
  }
}

// ── the tap machine ─────────────────────────────────────────────────────────────
// A turn is one packed ply, and the player builds it up by tapping. The common case is TWO taps —
// pick a piece, pick a square — and the machine only asks a third question when the answer is
// genuinely ambiguous: when both of your tolls reach the same square (so which one do you pay?),
// and in Arrears, when there are two tolls on the table (so which one do you pick up?). Asking
// only when it matters is the difference between a board game and a form.
interface TurnUi {
  /** Board cell of the piece the player has picked up. */
  sel: number | null;
  /** Optional pre-filter: the player tapped one of their toll cards first. */
  filter: number | null;
  /** A move that is decided except for one remaining question. */
  pending: { from: number; to: number; cands: number[]; ask: 'pay' | 'take' } | null;
}

const freshTurn = (): TurnUi => ({ sel: null, filter: null, pending: null });

const distinct = (xs: number[]): number[] => [...new Set(xs)];

// ── the game screen ─────────────────────────────────────────────────────────────
interface Screen {
  destroy(): void;
}

let activeDuel: Duel | null = null;
let activeSubmit: ((ply: number) => boolean) | null = null;

function buildGameScreen(d: Duel, onQuit: () => void, onOver: () => void): Screen {
  const mode = d.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-turn" id="hudTurn"></span>
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <button class="iconbtn" id="pause" title="Pause" aria-label="Pause">❚❚</button>
      </div>
      <div class="clockbar" id="clockbar" hidden><i id="clockfill"></i></div>
      <div class="hand rival" id="rivalHand"></div>
      <div class="board-host" id="boardHost"></div>
      <div class="hand mine" id="myHand"></div>
      <p class="prompt" id="prompt" aria-live="polite"></p>
      <div class="turn-actions" id="actions"></div>
      <aside class="side" id="side"><h3>Turns</h3><ul class="movelist" id="movelist"></ul></aside>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const hudTurn = content.querySelector<HTMLElement>('#hudTurn')!;
  const promptEl = content.querySelector<HTMLElement>('#prompt')!;
  const rivalHand = content.querySelector<HTMLElement>('#rivalHand')!;
  const myHand = content.querySelector<HTMLElement>('#myHand')!;
  const actions = content.querySelector<HTMLElement>('#actions')!;
  const clockbar = content.querySelector<HTMLElement>('#clockbar')!;
  const clockfill = content.querySelector<HTMLElement>('#clockfill')!;
  const movelist = content.querySelector<HTMLElement>('#movelist')!;

  const view = new BoardView(boardHost, (cell) => onCell(cell), reducedMotion);
  view.setFlip(d.mySeat === 1);
  let ui = freshTurn();
  let live = false;
  let destroyed = false;
  let last: LastMove | null = null;
  let botTimer: ReturnType<typeof setTimeout> | null = null;

  /** Which seat's hand the local player is allowed to see as "mine". Spectators watch seat 0. */
  const meSeat = (): number => d.mySeat ?? 0;

  const cd = runCountdown(boardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    paint();
    maybeBot();
  });

  const legalNow = (): number[] => (live ? d.legal() : []);

  const boardAdapter = {
    occ: d.state.occ,
    pos: d.state.pos,
    coffers: COFFER,
    keeperOf: (p: number) => isKeeper(p),
    seatOf: (p: number) => seatOfPiece(p),
  };

  function hints(): ViewHints {
    const s = d.state;
    const h = emptyHints();
    h.last = last;
    h.mySeat = d.mySeat;
    h.interactive = live && d.isMyTurn && s.phase === PH_PLAY;
    h.winCell = isOver(s) && s.winCell >= 0 ? s.winCell : null;
    if (!h.interactive) return h;

    const legal = legalNow().filter((p) => plyKind(p) === K_MOVE && (ui.filter === null || plyHand(p) === ui.filter));
    for (const p of legal) h.movable.add(plyFrom(p));
    h.selected = ui.sel;
    if (ui.sel === null) return h;
    for (const p of legal) {
      if (plyFrom(p) !== ui.sel) continue;
      const to = plyTo(p);
      if (s.occ[to] >= 0) h.takeTargets.add(to);
      else h.moveTargets.add(to);
    }
    return h;
  }

  function paint(): void {
    if (destroyed) return;
    const s = d.state;
    boardAdapter.occ = s.occ;
    boardAdapter.pos = s.pos;
    view.paint(boardAdapter, hints());

    const seat = s.turn;
    const yours = d.mySeat === seat;
    hudTurn.innerHTML =
      d.fault !== null
        ? '<span class="fault-note">Round stopped</span>'
        : isOver(s)
          ? `<span>${escapeHtml(s.winner >= 0 ? `${d.names[s.winner]} wins` : 'Round over')}</span>`
          : `<span class="rtok p${seat + 1}"></span><span>${escapeHtml(
              d.mySeat === null ? `${d.names[seat]} to play` : yours ? 'Your turn' : `${d.names[seat]} is thinking…`,
            )}</span>`;

    promptEl.textContent = promptText();
    renderHands();
    renderActions();
    renderClock();
    renderMoves();
    renderChooser();
  }

  function promptText(): string {
    const s = d.state;
    if (d.fault === 'build') return 'Your opponent is on a different build of Tithe — reload to play again.';
    if (d.fault === 'desync') return 'The two boards stopped agreeing, so the round was stopped.';
    if (d.missing !== null) return `${d.names[d.missing]} dropped out — holding the round for ${Math.ceil(d.holdLeft / 1000)}s…`;
    if (isOver(s)) return '';
    if (!live) return '';
    if (d.mySeat === null) return 'Watching — you are in the next round.';
    if (!d.isMyTurn) return `Waiting for ${d.names[s.turn]}…`;
    if (s.phase === PH_SPLIT) return 'Cut the tolls into two pairs. Your opponent picks one — and moves first.';
    if (s.phase === PH_CHOOSE) return 'Take a pair. You move first.';
    if (ui.pending) return ui.pending.ask === 'pay' ? 'Which toll do you pay?' : 'Which toll do you take from the table?';
    const legal = legalNow();
    if (legal.length > 0 && legal.every((p) => plyKind(p) === K_SLACK)) return 'Nothing can move — pay a toll of your choosing.';
    if (ui.sel === null) return 'Tap one of your pieces.';
    return 'Tap a highlighted square.';
  }

  function renderHands(): void {
    const s = d.state;
    const me = meSeat();
    const foe = 1 - me;

    rivalHand.innerHTML = [0, 1]
      .map((i) => tollHtml(s.hand[foe * 2 + i] < 0 ? 0 : s.hand[foe * 2 + i], { flip: true, label: `${d.names[foe]}'s toll` }))
      .join('');

    const table = s.table.map((t, i) => tollHtml(t, { klass: 'float', label: `On the table: ${tollOf(t).name}`, action: `table:${i}` }));
    const mine = [0, 1].map((i) => {
      const id = s.hand[me * 2 + i];
      const usable = live && d.isMyTurn && s.phase === PH_PLAY && !ui.pending;
      return tollHtml(id < 0 ? 0 : id, {
        action: `hand:${i}`,
        selected: ui.filter === i,
        disabled: !usable,
        label: `Your toll ${tollOf(id < 0 ? 0 : id).name}`,
      });
    });
    myHand.innerHTML = `${table.join('')}<span class="sep" aria-hidden="true">TABLE</span>${mine.join('')}`;

    myHand.querySelectorAll<HTMLElement>('[data-toll^="hand:"]').forEach((b) =>
      b.addEventListener('click', () => {
        const i = Number(b.dataset.toll!.split(':')[1]);
        ui.filter = ui.filter === i ? null : i;
        ping('blip', 1.1);
        paint();
      }),
    );
  }

  function renderChooser(): void {
    const old = boardHost.querySelector<HTMLElement>('.pick-ov');
    if (!ui.pending) {
      old?.remove();
      return;
    }
    // In Arrears a move can ask TWO questions in a row — which toll do you pay, then which do you
    // take off the table. Keeping the existing overlay because "one is already open" left the
    // second question showing the FIRST question's cards: the prompt said "take from the table"
    // while the player was looking at their own hand. Re-render whenever the question changes.
    if (old && old.dataset.ask === ui.pending.ask) return;
    old?.remove();
    const s = d.state;
    const me = meSeat();
    const p = ui.pending;
    const opts =
      p.ask === 'pay'
        ? distinct(p.cands.map(plyHand)).map((h) => ({ v: h, id: s.hand[me * 2 + h] }))
        : distinct(p.cands.map(plyTake)).map((t) => ({ v: t, id: s.table[t] }));
    const ov = document.createElement('div');
    ov.className = 'pick-ov';
    ov.dataset.ask = p.ask;
    ov.innerHTML = opts.map((o) => tollHtml(o.id, { action: `pick:${o.v}`, label: tollOf(o.id).name })).join('');
    boardHost.appendChild(ov);
    ov.querySelectorAll<HTMLElement>('[data-toll^="pick:"]').forEach((b) =>
      b.addEventListener('click', () => {
        const v = Number(b.dataset.toll!.split(':')[1]);
        const narrowed = p.cands.filter((c) => (p.ask === 'pay' ? plyHand(c) : plyTake(c)) === v);
        ui.pending = null;
        resolve(p.from, p.to, narrowed);
      }),
    );
  }

  function renderActions(): void {
    const canUndo = d.isMyTurn && (ui.sel !== null || ui.filter !== null || ui.pending !== null);
    actions.innerHTML = `${canUndo ? '<button class="btn" id="undo">Back</button>' : ''}<button class="btn ghost" id="quit">Quit</button>`;
    actions.querySelector('#undo')?.addEventListener('click', () => {
      if (ui.pending) ui.pending = null;
      else if (ui.sel !== null) ui.sel = null;
      else ui.filter = null;
      ping('blip', 0.7);
      paint();
    });
    actions.querySelector('#quit')!.addEventListener('click', onQuit);
  }

  function renderClock(): void {
    if (d.clockMs <= 0 || isOver(d.state) || d.mySeat === null) {
      clockbar.hidden = true;
      return;
    }
    clockbar.hidden = !d.isMyTurn;
    const frac = Math.max(0, Math.min(1, d.turnLeft / d.clockMs));
    clockfill.style.width = `${frac * 100}%`;
    clockbar.classList.toggle('low', frac < 0.25);
  }

  function renderMoves(): void {
    const s = d.state;
    const rows: string[] = [];
    const probe = { n: 0 };
    // Rebuild the text from the log rather than accumulating it, so a peer that joined mid-round
    // and replayed shows the same list as one that was there from the start.
    const seen = s.log.filter((p) => plyKind(p) === K_MOVE || plyKind(p) === K_SLACK);
    for (const ply of seen.slice(-24)) {
      probe.n++;
      rows.push(
        `<li>${plyKind(ply) === K_SLACK ? 'pays a toll, nothing moves' : `${cellName(plyFrom(ply))}→${cellName(plyTo(ply))}`}</li>`,
      );
    }
    movelist.innerHTML = rows.join('');
  }

  function juice(events: PlyEvent[]): void {
    for (const e of events) {
      if (e.kind === 'move') ping('blip', 1);
      else if (e.kind === 'take') {
        ping('thud', e.keeper ? 0.5 : 0.9);
        view.shake(e.keeper ? 6 : 3);
        if (e.to !== undefined) view.burst(e.to, e.keeper ? PALETTE.gold : PALETTE.alarm, e.keeper ? 26 : 12);
      } else if (e.kind === 'pay') {
        ping('blip', 1.35);
      } else if (e.kind === 'levy') {
        ping('thud', 0.6);
        view.shake(4);
        if (e.to !== undefined) view.burst(e.to, PALETTE.textMuted, 14);
      } else if (e.kind === 'seize' || e.kind === 'collect' || e.kind === 'drained') {
        ping('win');
        view.flash();
        if (e.to !== undefined) view.burst(e.to, PALETTE.gold, 30);
      } else if (e.kind === 'slack') {
        ping('thud', 0.7);
      }
      if (!reducedMotion && 'vibrate' in navigator) {
        try {
          if (e.kind === 'seize' || e.kind === 'collect') navigator.vibrate([12, 30, 12]);
          else if (e.kind === 'take') navigator.vibrate(8);
        } catch {
          /* haptics are best-effort */
        }
      }
    }
  }

  function submit(ply: number): boolean {
    const s = d.state;
    const wasMove = plyKind(ply) === K_MOVE ? { from: plyFrom(ply), to: plyTo(ply), seat: s.turn } : null;
    const ok = d.submit(ply);
    if (!ok) return false;
    if (wasMove) last = wasMove;
    ui = freshTurn();
    paint();
    maybeBot();
    return true;
  }
  activeSubmit = submit;

  /** Everything decided except possibly one question. Ask it, or commit. */
  function resolve(from: number, to: number, cands: number[]): void {
    if (cands.length === 0) return;
    if (distinct(cands.map(plyHand)).length > 1) {
      ui.pending = { from, to, cands, ask: 'pay' };
      ping('blip', 1.2);
      paint();
      return;
    }
    if (distinct(cands.map(plyTake)).length > 1) {
      ui.pending = { from, to, cands, ask: 'take' };
      ping('blip', 1.2);
      paint();
      return;
    }
    submit(cands[0]);
  }

  function onCell(cell: number): void {
    if (!live || destroyed || !d.isMyTurn || ui.pending) return;
    const s = d.state;
    if (s.phase !== PH_PLAY) return;
    const legal = legalNow().filter((p) => plyKind(p) === K_MOVE && (ui.filter === null || plyHand(p) === ui.filter));

    const who = s.occ[cell];
    if (who >= 0 && seatOfPiece(who) === s.turn) {
      if (!legal.some((p) => plyFrom(p) === cell)) {
        view.nudge(cell);
        ping('thud', 0.6);
        return;
      }
      ui.sel = ui.sel === cell ? null : cell;
      ping('select');
      paint();
      return;
    }
    if (ui.sel === null) {
      view.nudge(cell);
      return;
    }
    const cands = legal.filter((p) => plyFrom(p) === ui.sel && plyTo(p) === cell);
    if (cands.length === 0) {
      view.nudge(cell);
      ping('thud', 0.6);
      return;
    }
    resolve(ui.sel, cell, cands);
  }

  function maybeBot(): void {
    if (destroyed || !live || !d.isBotTurn || isOver(d.state)) return;
    if (botTimer !== null) return;
    d.botThinking = true;
    // setTimeout, never rAF: a backgrounded tab throttles rAF to nothing and the opponent's turn
    // would simply never arrive.
    botTimer = setTimeout(() => {
      botTimer = null;
      if (destroyed || isOver(d.state)) return;
      const ply = chooseAny(d.state, optsForTier(tierOf(myTier), d.state.seed + d.state.ply));
      d.botThinking = false;
      if (ply >= 0) submit(ply);
    }, 340);
  }

  // Measure the REAL elapsed time rather than trusting the interval. A backgrounded tab clamps
  // timers to about once a second, so feeding a hard-coded dt makes every clock run at a fraction
  // of speed — a twenty-second disconnect hold takes eighty real seconds, which reads exactly like
  // it never fires at all.
  let lastTick = Date.now();
  const clock = setInterval(() => {
    if (destroyed) return;
    const now = Date.now();
    const dt = Math.min(5000, Math.max(0, now - lastTick));
    lastTick = now;
    d.tick(dt);
  }, 250);

  function setPaused(on: boolean): void {
    let ov = boardHost.querySelector<HTMLElement>('.pause-ov');
    if (on) {
      if (ov) return;
      ov = document.createElement('div');
      ov.className = 'pause-ov';
      ov.innerHTML = `<div class="pause-card"><h2>Paused</h2>
        <button class="btn primary" id="resume">Resume</button>
        <button class="btn ghost" id="pquit">Quit</button></div>`;
      boardHost.appendChild(ov);
      ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
      ov.querySelector('#pquit')!.addEventListener('click', onQuit);
    } else {
      ov?.remove();
    }
  }
  content.querySelector('#pause')!.addEventListener('click', () => setPaused(!boardHost.querySelector('.pause-ov')));

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'p' || e.key === 'P') setPaused(!boardHost.querySelector('.pause-ov'));
    else if (e.key === 'Escape' || e.key === 'u' || e.key === 'U') {
      if (ui.pending) ui.pending = null;
      else if (ui.sel !== null) ui.sel = null;
      else ui.filter = null;
      paint();
    } else if (e.key === '1' || e.key === '2') {
      const i = Number(e.key) - 1;
      ui.filter = ui.filter === i ? null : i;
      paint();
    }
  }
  window.addEventListener('keydown', onKey);

  d.deps.onChange = paint;
  d.deps.onEvents = (events) => juice(events);
  d.deps.onEnd = () => {
    setTimeout(() => {
      if (!destroyed) onOver();
    }, 900);
  };

  paint();

  return {
    destroy() {
      destroyed = true;
      clearInterval(clock);
      if (botTimer !== null) clearTimeout(botTimer);
      cd.cancel();
      view.destroy();
      window.removeEventListener('keydown', onKey);
      if (activeSubmit === submit) activeSubmit = null;
    },
  };
}

// ── the opening: cut and choose ─────────────────────────────────────────────────
/**
 * The pie cut, on screen. Seat 0 taps two tolls to make the first pair and two more to make the
 * second; whatever is left goes on the table. Seat 1 then takes a pair — and moves first, which is
 * what the cutter is being paid to price correctly.
 */
function buildOpeningScreen(d: Duel, onQuit: () => void): Screen {
  content.innerHTML = `
    <section class="screen game opening">
      <div class="hud"><span class="hud-turn" id="hudTurn"></span><span class="hud-mode">${escapeHtml(d.mode.name)}</span></div>
      <p class="prompt" id="prompt" aria-live="polite"></p>
      <div class="hand mine" id="deal"></div>
      <div class="turn-actions" id="actions"></div>
    </section>`;
  const dealEl = content.querySelector<HTMLElement>('#deal')!;
  const promptEl = content.querySelector<HTMLElement>('#prompt')!;
  const hudTurn = content.querySelector<HTMLElement>('#hudTurn')!;
  const actions = content.querySelector<HTMLElement>('#actions')!;
  let picks: number[] = [];
  let destroyed = false;
  let botTimer: ReturnType<typeof setTimeout> | null = null;

  function commitSplit(): void {
    const all = partitions(d.state.deal.length);
    const a = [picks[0], picks[1]].sort((x, y) => x - y);
    const b = [picks[2], picks[3]].sort((x, y) => x - y);
    const [p, q] = a[0] < b[0] ? [a, b] : [b, a];
    const idx = all.findIndex((t) => t[0] === p[0] && t[1] === p[1] && t[2] === q[0] && t[3] === q[1]);
    picks = [];
    if (idx >= 0) {
      ping('select');
      d.submit(packSplit(idx));
    }
    paint();
  }

  function paint(): void {
    if (destroyed) return;
    const s = d.state;
    const mine = d.mySeat !== null && s.turn === d.mySeat;
    hudTurn.textContent = s.phase === PH_SPLIT ? 'Cutting the tolls' : 'Taking a pair';

    if (s.phase === PH_SPLIT) {
      promptEl.textContent = mine
        ? picks.length < 2
          ? 'Tap two tolls for the first pair.'
          : 'Now tap two more for the second pair. What is left goes on the table.'
        : `Waiting for ${d.names[0]} to cut…`;
      dealEl.innerHTML = s.deal
        .map((id, i) =>
          tollHtml(id, {
            action: `deal:${i}`,
            selected: picks.includes(i),
            disabled: !mine,
            klass: picks.slice(0, 2).includes(i) ? 'pair-a' : picks.slice(2).includes(i) ? 'pair-b' : '',
          }),
        )
        .join('');
      dealEl.querySelectorAll<HTMLElement>('[data-toll^="deal:"]').forEach((b) =>
        b.addEventListener('click', () => {
          const i = Number(b.dataset.toll!.split(':')[1]);
          if (picks.includes(i)) picks = picks.filter((x) => x !== i);
          else if (picks.length < 4) picks.push(i);
          ping('blip', 1 + picks.length * 0.1);
          if (picks.length === 4) commitSplit();
          else paint();
        }),
      );
      actions.innerHTML = `${mine ? '<button class="btn" id="auto">Cut for me</button>' : ''}<button class="btn ghost" id="quit">Quit</button>`;
      actions.querySelector('#auto')?.addEventListener('click', () => {
        const ply = chooseAny(d.state, optsForTier(tierOf('adept'), d.state.seed));
        if (ply >= 0) d.submit(ply);
        picks = [];
        paint();
      });
    } else {
      const offer = s.offer ?? [[], []];
      promptEl.textContent = mine ? 'Take a pair — and the first move.' : `Waiting for ${d.names[1]} to take a pair…`;
      dealEl.innerHTML = offer
        .map(
          (pair, i) =>
            `<button type="button" class="pairbtn" data-pair="${i}"${mine ? '' : ' disabled'} aria-label="Take pair ${i + 1}">${pair
              .map((id) => tollHtml(id))
              .join('')}</button>`,
        )
        .join('');
      dealEl.querySelectorAll<HTMLElement>('[data-pair]').forEach((b) =>
        b.addEventListener('click', () => {
          ping('select');
          d.submit(packChoose(Number(b.dataset.pair)));
          paint();
        }),
      );
      actions.innerHTML = `<button class="btn ghost" id="quit">Quit</button>`;
    }
    actions.querySelector('#quit')!.addEventListener('click', onQuit);
    maybeBot();
  }

  function maybeBot(): void {
    if (destroyed || !d.isBotTurn || botTimer !== null) return;
    botTimer = setTimeout(() => {
      botTimer = null;
      if (destroyed) return;
      const ply = chooseAny(d.state, optsForTier(tierOf(myTier), d.state.seed + d.state.ply));
      if (ply >= 0) d.submit(ply);
      paint();
    }, 420);
  }

  d.deps.onChange = paint;
  paint();

  return {
    destroy() {
      destroyed = true;
      if (botTimer !== null) clearTimeout(botTimer);
    },
  };
}

/** Run the opening, then hand over to the board. One place, so solo and network share it. */
function runRound(d: Duel, onQuit: () => void, onOver: () => void): void {
  let screen: Screen | null = null;
  const mountGame = (): void => {
    screen?.destroy();
    screen = buildGameScreen(d, onQuit, onOver);
  };
  if (d.state.phase === PH_PLAY) mountGame();
  else {
    screen = buildOpeningScreen(d, onQuit);
    const watch = setInterval(() => {
      if (d.state.phase === PH_PLAY) {
        clearInterval(watch);
        mountGame();
      }
    }, 120);
    onCleanup(() => clearInterval(watch));
  }
  onCleanup(() => screen?.destroy());
}

// ── solo ────────────────────────────────────────────────────────────────────────
function startSolo(): void {
  teardown();
  setPlaying(true);
  soloRound++;
  const mode = modeOf(myModeChoice);
  // Alternate which seat the player takes, so cutting and choosing both come round.
  const mySeat = soloRound % 2 === 1 ? 0 : 1;
  const names: [string, string] = mySeat === 0 ? [playerName, `${tierOf(myTier).name}`] : [`${tierOf(myTier).name}`, playerName];
  const d = new Duel({
    mode,
    seed: newSeed(),
    round: soloRound,
    names,
    mySeat,
    bot: optsForTier(tierOf(myTier)),
    deps: { send: () => {}, onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  duel = d;
  activeDuel = d;
  runRound(
    d,
    () => showMenu(),
    () => {
      if (d.state.winner >= 0) noteResult(mode.id, d.state.winner === mySeat);
      showResults(false);
    },
  );
}

// ── multiplayer ─────────────────────────────────────────────────────────────────
let netSend: (m: WireMsg) => void = () => {};
let mvOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let beat: ReturnType<typeof setInterval> | null = null;

function repaintLobby(): void {
  if (lobbyMounted) lobby?.repaint();
}

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with a friend',
    subtitle: 'Start a room and send the link, or type a code you were given.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [0, 0];
  duel = null;
  activeDuel = null;

  content.innerHTML = `<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>`;

  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: () => repaintLobby(),
      onPeers: () => {
        repaintLobby();
        duel?.peerBack();
      },
      onPeerLeave: (id) => {
        const idx = roster.findIndex((p) => p.id === id);
        if (idx >= 0 && duel) duel.peerLeft(seatForRound(idx, roundNo));
        repaintLobby();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
      refreshResults();
    },
  });
  rounds = r;

  const send = n.channel<WireMsg>('mv', (data) => duel?.receive(data));
  netSend = (m) => send(m);
  mvOff = () => send.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `<section class="screen"><div id="lobby-host"></div></section>`;
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 2,
    onCancel: () => void leaveRoom(),
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === myModeChoice}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      return `<p class="host-pick">${n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'}</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  roundNo = info.round;
  const mode = modeOf(info.opts?.mode);
  const myIndex = roster.findIndex((p) => p.id === (net?.selfId ?? ''));
  const seated = info.seated && myIndex >= 0;
  const mySeat = seated ? seatForRound(myIndex, info.round) : null;
  const names: [string, string] = ['', ''];
  roster.slice(0, 2).forEach((p, i) => {
    const seat = seatForRound(i, info.round);
    // Two players CAN arrive with the same handle — the default one is random, and two tabs on one
    // device share the store outright. Identical names on a results screen make it unreadable.
    names[seat] = p.name || SEAT_NAME[seat];
  });
  if (!names[0]) names[0] = SEAT_NAME[0];
  if (!names[1]) names[1] = SEAT_NAME[1];
  if (names[0] === names[1]) {
    names[0] = `${names[0]} (${SEAT_NAME[0]})`;
    names[1] = `${names[1]} (${SEAT_NAME[1]})`;
  }

  const d = new Duel({
    mode,
    seed: info.seed,
    round: info.round,
    names,
    mySeat,
    bot: null,
    clockMs: 60_000,
    deps: { send: (m) => netSend(m), onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  duel = d;
  activeDuel = d;
  setPlaying(true);

  if (beat !== null) clearInterval(beat);
  // The heartbeat is what makes a lost message a non-event: it re-sends the whole log every 2s.
  beat = setInterval(() => d.broadcast(), 2000);
  onCleanup(() => {
    if (beat !== null) clearInterval(beat);
    beat = null;
  });

  runRound(
    d,
    () => {
      // End this peer's round FIRST (phase playing → waiting) or the engine lobby paints nothing
      // for a peer still seated in a live round, and the player gets a blank screen.
      rounds?.finish();
      backToLobby();
    },
    () => showResults(true),
  );
  d.broadcast();
}

// ── results ─────────────────────────────────────────────────────────────────────
let resultsMounted = false;
let multi = false;

function showResults(isMulti: boolean): void {
  teardown();
  setPlaying(false);
  multi = isMulti;
  resultsMounted = true;
  const d = duel;
  if (!d) {
    showMenu();
    return;
  }
  if (d.state.winner >= 0 && d.state.reason !== 'nocontest') tally[d.state.winner]++;
  if (isMulti) rounds?.finish();

  content.innerHTML = `
    <section class="screen results-screen" id="res"></section>
    <div class="menu-actions" id="ract"></div>`;
  renderResults();
  onCleanup(() => {
    resultsMounted = false;
  });
}

function renderResults(): void {
  const d = duel;
  if (!d || !resultsMounted) return;
  const box = content.querySelector<HTMLElement>('#res');
  const act = content.querySelector<HTMLElement>('#ract');
  if (!box || !act) return;

  const a = analyse(d.mode.id, d.state.seed, d.state.log);
  box.innerHTML = resultsHtml({
    rows: d.results(),
    mode: d.mode,
    reason: d.reason,
    winner: d.state.winner,
    tally,
    round: multi ? roundNo : soloRound,
    fault: d.fault,
    missed: a.missed,
    missedNote: missedNote(a, d.names),
  });
  const cv = box.querySelector<HTMLCanvasElement>('#finalBoard');
  if (cv) drawFinal(cv, d.state);

  act.innerHTML = multi
    ? `<button class="btn primary" id="again">Play again</button>
       <p class="waiting-note" id="waiting" aria-live="polite"></p>
       <button class="btn" id="lobby">Back to lobby</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`
    : `<button class="btn primary" id="again">Play again</button>
       <button class="btn" id="share">Share</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`;

  act.querySelector('#feedback')!.addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
  act.querySelector('#menu')!.addEventListener('click', () => (multi ? void leaveRoom() : showMenu()));
  if (multi) {
    const again = act.querySelector<HTMLButtonElement>('#again')!;
    again.addEventListener('click', () => {
      rounds?.vote();
      again.disabled = true;
      again.textContent = 'Waiting for the others…';
      refreshResults();
    });
    act.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
    refreshResults();
  } else {
    act.querySelector('#again')!.addEventListener('click', () => startSolo());
    act.querySelector('#share')!.addEventListener('click', () => void share());
  }
}

/** The final position, drawn once. A results screen with no board is a scoreboard, not a game. */
function drawFinal(cv: HTMLCanvasElement, s: State): void {
  const ctx = cv.getContext('2d');
  if (!ctx) return;
  const n = 5;
  const pad = 24;
  const cell = (cv.width - pad * 2) / n;
  ctx.fillStyle = PALETTE.gutter;
  ctx.fillRect(0, 0, cv.width, cv.height);
  for (let c = 0; c < 25; c++) {
    const x = pad + (c % n) * cell;
    const y = pad + Math.floor(c / n) * cell;
    ctx.fillStyle = COFFER.includes(c as 2 | 22) ? PALETTE.coffer : PALETTE.stone;
    ctx.fillRect(x + 3, y + 3, cell - 6, cell - 6);
    const p = s.occ[c];
    if (p < 0) continue;
    const seat = seatOfPiece(p);
    ctx.fillStyle = SEAT_COLOUR[seat];
    const cxp = x + cell / 2;
    const cyp = y + cell / 2;
    const r = cell * 0.32;
    ctx.beginPath();
    if (seat === 0) ctx.arc(cxp, cyp, r, 0, Math.PI * 2);
    else {
      ctx.moveTo(cxp, cyp - r);
      ctx.lineTo(cxp + r, cyp);
      ctx.lineTo(cxp, cyp + r);
      ctx.lineTo(cxp - r, cyp);
      ctx.closePath();
    }
    ctx.fill();
    if (isKeeper(p)) {
      ctx.strokeStyle = PALETTE.gold;
      ctx.lineWidth = Math.max(3, cell * 0.07);
      ctx.beginPath();
      ctx.arc(cxp, cyp, r * 0.55, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
}

function refreshResults(): void {
  if (!resultsMounted || !multi) return;
  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (!waiting) return;
  const r = rounds?.state();
  if (r && r.startsInMs !== null) {
    waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
  } else if (r && r.votes.length) {
    waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
  } else {
    waiting.textContent = '';
  }
}

setInterval(() => refreshResults(), 400);

async function share(): Promise<void> {
  const d = duel;
  if (!d) return;
  const me = d.mySeat ?? 0;
  const won = d.state.winner === me;
  const text = `Tithe · ${d.mode.name} — ${won ? 'won' : 'lost'} in ${d.results()[me].turns} turns\nhttps://tithe.benrichardson.dev`;
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

function backToLobby(): void {
  teardown();
  duel = null;
  activeDuel = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  // Take the room out of the URL on the way out, or a reload — or reopening from a home-screen
  // icon — silently drops the player back into a room they deliberately left.
  clearRoomInUrl();
  lobbyMounted = false;
  duel = null;
  activeDuel = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    mvOff();
  } catch {
    /* the channel may already be gone */
  }
  mvOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const params = new URL(location.href).searchParams;
  const room = params.get('room');
  if (room && normalizeRoomCode(room).length >= 3) showRoomEntry();
  else showMenu();
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// Exposed so the browser-verification pass can drive the REAL submit path synchronously — rAF is
// throttled in a hidden tab, and every phone check runs through this. Never read by gameplay.
(window as unknown as { __tithe?: unknown }).__tithe = {
  state(): unknown {
    const s = activeDuel?.state;
    if (!s) return null;
    return {
      mode: s.mode.id,
      phase: s.phase,
      turn: s.turn,
      ply: s.ply,
      turns: s.turns,
      levy: s.levy,
      over: isOver(s),
      winner: s.winner,
      reason: s.reason,
      occ: Array.from(s.occ),
      hand: Array.from(s.hand),
      table: s.table.slice(),
      deal: s.deal.slice(),
      mySeat: activeDuel?.mySeat ?? null,
      log: s.log.slice(),
      bearers: [activeDuel!.bearers(0), activeDuel!.bearers(1)],
    };
  },
  legal(): number[] {
    return activeDuel?.legal() ?? [];
  },
  play(ply: number): boolean {
    return activeSubmit ? activeSubmit(ply) : false;
  },
  /** Play one legal ply chosen by the shipped search — enough to walk a whole game in a pane. */
  auto(): boolean {
    const d = activeDuel;
    if (!d || isOver(d.state)) return false;
    const ply = chooseAny(d.state, { depth: 2, timeMs: 0, nodes: 20_000 });
    if (ply < 0) return false;
    if (d.state.phase !== PH_PLAY) return d.submit(ply);
    return !!activeSubmit && activeSubmit(ply);
  },
  colours: SEAT_COLOUR,
  PPS,
};

boot();
